<template>
  <div class="terms-container">
    <p class="scroll-notice">
      Please scroll down to read the entire Terms of Service, then check the
      box and click Accept to continue.
    </p>
    <div
      v-if="termsOfServiceData?.html"
      class="terms-content"
      ref="termsContainer"
      @scroll="onScroll"
    >
      <div v-html="termsOfServiceData.html"></div>
    </div>
    <div v-else class="loading">Loading Terms of Service…</div>

    <div class="agreement-section">
      <label style="color: var(--fg1)">
        <input type="checkbox" v-model="agreed" :disabled="!scrolledToBottom" />
        I have read and agree to the Terms and Conditions
      </label>
      <form action="/terms-of-service/accept" method="POST">
        <input
          type="hidden"
          name="session_token"
          :value="route.query.session_token"
        />
        <input type="hidden" name="state" :value="route.query.state" />
        <button
          type="submit"
          class="submit-button"
          :disabled="!scrolledToBottom || !agreed"
        >
          Accept
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import termsHtml from "@/assets/terms_v1.html?raw";
const route = useRoute();
const termsOfServiceData = ref(null);
const termsContainer = ref(null);
const agreed = ref(false);
const scrolledToBottom = ref(false);

function onScroll() {
  const el = termsContainer.value;
  if (!el) return;

  const atBottom =
    Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight - 10;

  scrolledToBottom.value = atBottom;
}

onMounted(async () => {
  try {
    termsOfServiceData.value = { html: termsHtml };
  } catch (e) {
    console.error(e);
  }
});
</script>

<style scoped>
.scroll-notice {
  color: var(--fg1);
  font-weight: 600;
  text-align: center;
  margin: 0 1rem 0.75rem;
}

.terms-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  max-height: 100vh;
  background-color: var(--bg1, #f5f5f5);
  overflow: hidden;
  padding-top: 1rem;
  margin: 1rem 0;
}

.terms-content {
  flex: 1;
  background-color: #fff;
  font-size: 16px;
  line-height: 1.6;
  max-width: 900px;
  width: 90%;
  padding: 1rem;
  overflow-y: auto;
  overflow-x: auto;
  color: #111;
  box-sizing: border-box;
}

/* Force all HTML content to respect container width */
.terms-content :deep(*) {
  max-width: 100% !important;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Typography */
.terms-content :deep(h1) {
  font-size: 1.75rem;
  margin: 1.5rem 0 1rem;
  font-weight: 700;
  line-height: 1.2;
}

.terms-content :deep(h2) {
  font-size: 1.5rem;
  margin: 1.25rem 0 0.75rem;
  font-weight: 600;
  line-height: 1.3;
}

.terms-content :deep(h3) {
  font-size: 1.25rem;
  margin: 1rem 0 0.5rem;
  font-weight: 600;
  line-height: 1.3;
}

.terms-content :deep(p) {
  margin-bottom: 1rem;
}

.terms-content :deep(ul),
.terms-content :deep(ol) {
  margin: 1rem 0;
  padding-left: 2rem;
}

.terms-content :deep(li) {
  margin-bottom: 0.5rem;
}

/* Break long URLs */
.terms-content :deep(a) {
  color: #007bff;
}

.loading {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--fg1, #666);
  font-size: 1.1rem;
}

.agreement-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--bg1, #fff);
  overflow-y: auto;
  overflow-x: auto; 
}

.agreement-section label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  color: var(--fg1, #333);
  cursor: pointer;
  user-select: none;
  line-height: 1.4;
}

.agreement-section input[type="checkbox"] {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.submit-button {
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  background-color: #007bff;
  color: white;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.submit-button:not(:disabled) {
  background-color: var(--revBlue) !important;
}

/* Mobile */
@media (max-width: 480px) {
  .terms-container {
    height: 100vh;
    margin: 0;
    padding-top: 0;
  }

  .terms-content {
    padding: 1rem;
    font-size: 14px;
    line-height: 1.5;
  }

  .terms-content :deep(h1) {
    font-size: 1.35rem;
  }

  .terms-content :deep(h2) {
    font-size: 1.2rem;
  }

  .terms-content :deep(h3) {
    font-size: 1.1rem;
  }

  .terms-content :deep(ul),
  .terms-content :deep(ol) {
    padding-left: 1.5rem;
  }

  .agreement-section {
    padding: 2rem;
    gap: 0.875rem;
  }

  .agreement-section label {
    font-size: 0.9rem;
    gap: 0.625rem;
  }

  .agreement-section input[type="checkbox"] {
    width: 18px;
    height: 18px;
  }

  .submit-button {
    width: 100%;
    padding: 1rem;
    font-size: 1rem;
  }
}
</style>
